// seedScorePages/SharedForms/SharedForm.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiAlertCircle } from 'react-icons/fi';
import './SharedForm.css';
import seedxpertLogo from '../../assets/images/dark-arkaplansiz.png';
import { useSharedForm } from '../../context/SharedFormContext';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import { useToast } from '../../context/ToastContext/ToastContext';

const SharedForm = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { addToast } = useToast();

    const { updateEntrepreneurInfo, updateSharedFinancials, updateSharedAssessment, clearSharedFormData } = useSharedForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [linkData, setLinkData] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    
    // Email state'leri
    const [entrepreneurEmail, setEntrepreneurEmail] = useState('');
    const [entrepreneurName, setEntrepreneurName] = useState('');
    const [emailChecked, setEmailChecked] = useState(false);
    const [emailExists, setEmailExists] = useState(false);

    const [hasDraft, setHasDraft] = useState(false);
    const [draftData, setDraftData] = useState(null);
    const verifiedTokenRef = useRef(null);

    const verifyLink = async () => {
        try {
            const response = await fetch(`/api/shared-links/verify/${token}`);
            const data = await response.json();

            if (response.ok) {
                setLinkData(data);
                setCurrentStep(1);
            } else if (response.status === 410) {
                if (data.msg.includes('maksimum') || data.msg.includes('maximum')) {
                    setError(t('sharedForm.maxSubmissionsReached'));
                } else if (data.msg.includes('token')) {
                    setError(t('sharedForm.ownerTokenExpired'));
                } else {
                    setError(data.msg || t('sharedForm.linkExpired'));
                }
            } else {
                setError(data.msg || t('sharedForm.invalidLink'));
            }
        } catch (err) {
            setError(t('sharedForm.connectionError'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const completed = localStorage.getItem('sharedFormCompleted');
        if (completed === 'true') {
            window.location.href = `/shared-form/${token}/success`;
            return;
        }

        if (verifiedTokenRef.current === token) {
            return;
        }

        verifiedTokenRef.current = token;
        verifyLink();
    }, [token]);

    const handleStartForm = () => {
        localStorage.setItem('sharedFormEmail', entrepreneurEmail);
        localStorage.setItem('sharedFormName', entrepreneurName);
        localStorage.setItem('sharedFormToken', token);
        
        setTimeout(() => {
            navigate(`/shared-form/${token}/entrepreneur-info`);
        }, 100);
    };
     
    const checkEmail = async () => {
        if (!entrepreneurEmail) {
            addToast(t('sharedForm.enterEmailAlert'), 'error');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(entrepreneurEmail)) {
            addToast(t('sharedForm.invalidEmailAlert'), 'error');
            return;
        }

        try {
            const submissionCheck = await fetch(`/api/shared-links/check-email/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: entrepreneurEmail })
            });
            const submissionData = await submissionCheck.json();

            

           if (submissionData.notAllowed) {
                addToast(t('sharedForm.emailNotAllowed'), 'error');
                return; 
            }

            if (submissionData.exists) {
                setEmailExists(true);
               addToast(t('sharedForm.emailAlreadyUsed'), 'error');
                return;
            }

            const draftCheck = await fetch(`/api/shared-links/load-draft/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: entrepreneurEmail })
            });
            const draftResponse = await draftCheck.json();

            if (draftResponse.hasDraft) {
                setHasDraft(true);
                setDraftData(draftResponse);
                
                localStorage.setItem('sharedFormEmail', entrepreneurEmail);
                localStorage.setItem('sharedFormName', entrepreneurName);
                localStorage.setItem('sharedFormToken', token);
            } else {
                clearSharedFormData();
                
                localStorage.setItem('sharedFormEmail', entrepreneurEmail);
                localStorage.setItem('sharedFormName', entrepreneurName);
                localStorage.setItem('sharedFormToken', token);
                
                updateEntrepreneurInfo({
                    email: entrepreneurEmail,
                    contactName: entrepreneurName
                });
                
                setTimeout(() => {
                    navigate(`/shared-form/${token}/entrepreneur-info`);
                }, 200);
            }

        } catch (error) {
            addToast(t('sharedForm.errorOccurred'), 'error');
        }
    };

    const continueFromDraft = () => {
        localStorage.setItem('sharedFormEmail', entrepreneurEmail);
        localStorage.setItem('sharedFormName', entrepreneurName);
        localStorage.setItem('sharedFormToken', token);
        
        if (draftData.drafts['entrepreneur-info']) {
            updateEntrepreneurInfo(draftData.drafts['entrepreneur-info']);
        }
        if (draftData.drafts['financial-table']) {
            updateSharedFinancials(
                draftData.drafts['financial-table'].inputs,
                draftData.drafts['financial-table'].results
            );
        }
        if (draftData.drafts['assessment']) {
            updateSharedAssessment(
                draftData.drafts['assessment'].answers,
                draftData.drafts['assessment'].results
            );
        }

        setTimeout(() => {
            const stepRoutes = {
                'entrepreneur-info': `/shared-form/${token}/entrepreneur-info`,
                'financial-table': `/shared-form/${token}/financial-table`,
                'assessment': `/shared-form/${token}/assessment`
            };
            
            navigate(stepRoutes[draftData.lastStep]);
        }, 50);
    };

    const startFresh = () => {
        clearSharedFormData();
        
        localStorage.setItem('sharedFormEmail', entrepreneurEmail);
        localStorage.setItem('sharedFormName', entrepreneurName);
        localStorage.setItem('sharedFormToken', token);
        
        setHasDraft(false);
        handleStartForm();
    };

    const formatDate = (dateString) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Date(dateString).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        const locale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
        return new Date(dateString).toLocaleString(locale);
    };

    if (loading) {
        return (
            <div className="shared-form-container">
                <div className="shared-form-loading">
                    <div className="loading-spinner"></div>
                    <p>{t('sharedForm.loading')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="shared-form-container">
                <div className="shared-form-error">
                    <FiAlertCircle size={64} color="#e74c3c" />
                    <h2>{error}</h2>
                    <p>{t('sharedForm.linkNoLongerValid')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="shared-form-container">
            <div className="shared-form-header">
                <img src={seedxpertLogo} alt="SeedXpert" className="shared-form-logo" />
                <div className="shared-form-language-switcher">
                    <LanguageSwitcher />
                </div>
            </div>

            <div className="shared-form-content">
                {!hasDraft ? (
                    <div className="shared-form-welcome">
                        <h1>{linkData?.linkName || t('sharedForm.title')}</h1>
                        <p className="shared-form-type">
                            {t('sharedForm.applicationForm', {
                                defaultValue: i18n.language === 'tr' ? 'Başvuru Formu' : 'Application Form'
                            })}
                        </p>

                        {linkData?.description && (
                            <section className="shared-link-description" aria-label={t('sharedForm.applicationAbout', { defaultValue: 'Başvuru Hakkında' })}>
                                <h2>{t('sharedForm.applicationAbout', { defaultValue: 'Başvuru Hakkında' })}</h2>
                                <p>{linkData.description}</p>
                            </section>
                        )}

                        <div className="email-input-section">
                            <h3>{t('sharedForm.beforeStarting')}</h3>
                            <p>{t('sharedForm.enterEmail')}</p>
                            <input
                                type="email"
                                value={entrepreneurEmail}
                                onChange={(e) => setEntrepreneurEmail(e.target.value)}
                                placeholder={t('sharedForm.emailPlaceholder')}
                                className="email-input"
                            />
                         
                            
                            <div className="shared-form-info">
                                <p>
                                    <strong>{t('sharedForm.noteTitle')}</strong>{' '}
                                    {i18n.language?.startsWith('tr')
                                        ? 'Başvurunuzu tamamlamak için formun tüm adımlarını doldurmanız gerekmektedir. Çalışmanızı istediğiniz aşamada kaydedip daha sonra devam edebilirsiniz. Kaldığınız yerden devam etmek için formu yeniden açıp aynı e-posta adresini girmeniz yeterlidir.'
                                        : 'You need to complete all steps to submit your application. You can save your progress at any stage and continue later. To resume, reopen the form and enter the same email address.'}
                                </p>
                                <p className="expiry-info">
                                    {i18n.language?.startsWith('tr') ? 'Son başvuru tarihi:' : 'Application deadline:'} {formatDate(linkData?.expiresAt)}
                                </p>
                            </div>

                            <button 
                                className="shared-form-start-btn" 
                                onClick={checkEmail}
                            >
                                {t('sharedForm.startForm')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="shared-form-welcome">
                        <h1>{t('sharedForm.draftFoundTitle')}</h1>
                        
                        <div className="draft-found-section">
                            <div className="draft-info">
                                <p>
                                    {t('sharedForm.lastUpdate')} {formatDateTime(draftData.lastUpdated)}
                                </p>
                                <p>
                                    {t('sharedForm.lastStep')} <strong>{draftData.lastStep}</strong>
                                </p>
                            </div>
                            <div className="draft-actions">
                                <button 
                                    className="continue-draft-btn" 
                                    onClick={continueFromDraft}
                                >
                                    {t('sharedForm.continueFromDraft')}
                                </button>
                                <button 
                                    className="start-fresh-btn" 
                                    onClick={startFresh}
                                >
                                    {t('sharedForm.startFresh')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedForm;
